import { Can } from '@/components/can/Can';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ClubsIntoSubGroupForm } from '@/features/configuration-management/groups-management/ClubsIntoSubGroupForm';
import { useGroupDetail } from '@/features/configuration-management/groups-management/hooks/useGroupDetail';
import { useSubGroupsList } from '@/features/configuration-management/groups-management/hooks/useSubGroupsList';
import { SubGroupTable } from '@/features/configuration-management/groups-management/subgroups/table';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { StoreInformation } from '@/icons';
import { createFileRoute, SearchSchemaInput } from '@tanstack/react-router';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum(['subGroupInfo', 'totalClub']),
  desc: z.boolean(),
});

const GroupSearchScema = z.object({
  breadcrumbReturnTo: z.string().optional(),

  sort: z
    .preprocess((val) => {
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return undefined;
        }
      }
      return val;
    }, z.array(sortItemSchema))
    .optional(),

  q: z
    .any()
    .optional()
    .transform((val) => (val == null ? undefined : String(val))),

  createdDate: z.string().optional(),
  status: z
    .string()
    .transform((val) => val.split(','))
    .pipe(z.array(z.enum(['ACTIVE', 'INACTIVE'])))
    .optional(),
  locationFilter: z.string().optional(),

  page: z.coerce.number().positive().catch(0),
  fetch: z.coerce.number().positive().max(100).catch(10),
});

export const Route = createFileRoute('/(app)/configuration-management/group-management/$sub-group/')({
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => GroupSearchScema.parse(search),
});

function RouteComponent() {
  const { 'sub-group': groupId } = Route.useParams();
  const { groupDetail } = useGroupDetail(groupId);
  const defaultGroupDetailValues = {
    groupName: groupDetail?.groupName || '',
    groupNameKatakana: groupDetail?.groupNameKatakana || '',
  };
  const { data, pagination, isLoading, commonGroupId } = useSubGroupsList(groupId);
  const { onOpen } = useDialogFormStore();
  const { t } = useDynamicLocale();
  return (
    <div className="relative">
      <Can rule={{ allOf: [PERMS.group.assign.subgroup] }}>
        <Button
          className="absolute right-0 -top-[66px] border-foreground"
          variant="outline"
          onClick={() => {
            onOpen({
              title: 'Add New SubGroup',
              okText: t('common.page.modal.submit'),
              size: 'lg',
              modalTitle: 'configurationManagement.groupsManagement.manageSubGroups.title',
              component: (
                <ClubsIntoSubGroupForm
                  defaultGroupDetailValues={defaultGroupDetailValues}
                  groupId={groupId}
                  commonId={commonGroupId}
                />
              ),
            });
          }}
        >
          <StoreInformation />
          <Translation id="configurationManagement.groupsManagement.manageSubGroups.manageClubs" />
        </Button>
      </Can>
      <SubGroupTable
        isLoading={isLoading}
        data={data}
        pagination={pagination}
        groupId={groupId}
        defaultGroupDetailValues={defaultGroupDetailValues}
      />
    </div>
  );
}
